import { ActionIcon, Button, Textarea, TextInput, Tooltip } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useClickOutside, useDisclosure } from '@mantine/hooks';
import { IconMoodSmile } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmojiPicker } from 'src/Components/EmojiPicker';
import { useCreateCommunityFeedItem } from 'src/Hooks/Mutations/useCreateCommunityFeedItem';
import { useEditCommunityFeedItem } from 'src/Hooks/Mutations/useEditCommunityFeedItem';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import { useSelectedAssetId } from 'src/Hooks/Shared/useSelectedAssetId';
import { CreateCommunityFeedItemDtoTypeEnum } from 'src/Services/API/generated';
import { trackAlbumCreated } from 'src/Services/Mixpanel/events';

import { MemoryImagesListEdit } from './MemoryImagesListEdit';
import { Props } from './MemoryModalEdit.props';
import { FeedItemFile } from './MemoryModalEdit.types';
import styles from './MemoryModelEdit.module.scss';

/** Le nom de l'album est ce qui le rend reconnaissable dans le bandeau : il est requis. */
const ALBUM_NAME_MAX_LENGTH = 80;
const ALBUM_PLACE_MAX_LENGTH = 255;

/**
 * This component allow to create and edit a Memory (an album).
 */
const MemoryModalEdit = ({ feedItem, onFinish, onCancel }: Props) => {
  const { t } = useTranslation();

  const { mutate: mutateMemory, isLoading: isLoadingEdit } = useEditCommunityFeedItem({
    onSuccess(newFeedItem) {
      onFinish?.(newFeedItem);
    },
  });

  const [photos, setPhotos] = useState<FeedItemFile[]>(
    () => feedItem?.files.map(file => ({ id: file.id, serverFile: file })) ?? []
  );
  const [text, setText] = useState(feedItem?.content.en ?? '');
  const [description, setDescription] = useState(feedItem?.description ?? '');
  const [place, setPlace] = useState(feedItem?.place ?? '');
  // « Quand » est la date des photos, pas celle de la publication. À la création on part
  // d'aujourd'hui : l'EXIF n'est pas lisible ici sans décoder le fichier côté navigateur, et la
  // date reste modifiable à la main.
  const [takenAt, setTakenAt] = useState<Date | null>(
    feedItem?.takenAt ? dayjs(feedItem.takenAt).toDate() : new Date()
  );
  const [error, setError] = useState<string | null>(null);

  const [isEmojiPickerOpen, { close: closeEmojiPicker, toggle: toggleEmojiPicker }] =
    useDisclosure(false);
  const clickOutsideRef = useClickOutside(closeEmojiPicker);

  const assetId = useSelectedAssetId();
  const { mutate: createMemory, isLoading: isLoadingCreate } = useCreateCommunityFeedItem({
    onSuccess(newFeedItem) {
      trackAlbumCreated({ photos_count: photos.length });
      onFinish?.(newFeedItem);
    },
  });

  const onEmoji = (emoji: string) => {
    setText(t => t + emoji);
  };
  const showEmojiPicker = !useIsMobile();

  const feedItemToCreate = {
    type: CreateCommunityFeedItemDtoTypeEnum.PostMemory,
    message: text.trim(),
    files: photos.map(p => p.serverFile?.id ?? ''),
  };

  const onPublish = () => {
    closeEmojiPicker();

    if (text.trim().length === 0) {
      setError(t('community.albumNameRequired', "Donnez un nom à l'album avant de publier."));
      return;
    }

    if (photos.length === 0) {
      setError(
        t(
          'community.memory.photoMandatory',
          'Au moins une photo doit être enregistré avant de publier.'
        )
      );

      return;
    }

    // Can not publish while the photos are uploading
    if (photos.find(photo => !photo.serverFile)) {
      setError(
        t(
          'community.memory.photoStillUploading',
          'Les photos sont encore en cours d’envoi. Réessayez dans quelques secondes.'
        )
      );
      return;
    }

    // All good we can publish/edit the memory.

    if (!feedItem) {
      // Un album appartient à un seul bien : on publie sur le bien courant.
      if (!assetId) return;

      createMemory({
        type: CreateCommunityFeedItemDtoTypeEnum.PostMemory,
        message: feedItemToCreate.message,
        description: description.trim() || undefined,
        place: place.trim() || undefined,
        takenAt: takenAt?.toISOString(),
        files: feedItemToCreate.files,
        assets: [assetId],
      });
    } else {
      // Update the memory
      mutateMemory({
        feedItemId: feedItem.id,
        message: feedItemToCreate.message,
        description: description.trim(),
        place: place.trim(),
        takenAt: takenAt?.toISOString(),
        files: feedItemToCreate.files,
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.photos}>
        <MemoryImagesListEdit photos={photos} setPhotos={setPhotos} isEdit={!!feedItem} />
      </div>

      <div className={styles.rightColumn}>
        <TextInput
          label={t('community.albumTitle', "Titre de l'album")}
          placeholder={t('community.albumNamePlaceholder', 'Ex. Été 2026 à Ker à Pic')}
          withAsterisk
          maxLength={ALBUM_NAME_MAX_LENGTH}
          value={text}
          onChange={event => setText(event.currentTarget.value)}
        />

        <div className={styles.twoFields}>
          <DatePickerInput
            label={t('community.albumTakenAt', 'Quand')}
            valueFormat="DD/MM/YYYY"
            clearable
            value={takenAt}
            onChange={setTakenAt}
          />

          <TextInput
            label={t('community.albumPlace', 'Où')}
            placeholder={t('community.albumPlacePlaceholder', 'Ex. Ker à Pic')}
            maxLength={ALBUM_PLACE_MAX_LENGTH}
            value={place}
            onChange={event => setPlace(event.currentTarget.value)}
          />
        </div>

        <Textarea
          label={t('community.albumDescription', 'Description')}
          placeholder={t('community.albumDescriptionPlaceholder', 'Ex. Les cousins au complet.')}
          autosize
          minRows={3}
          maxRows={6}
          value={description}
          onChange={event => setDescription(event.currentTarget.value)}
        />

        {error ? <span className={styles.error}>{error}</span> : null}

        <div className={styles.buttonsRow} ref={clickOutsideRef}>
          {showEmojiPicker ? (
            <Tooltip label={t('community.albumEmoji', 'Ajouter un emoji au titre')} withArrow>
              <ActionIcon
                onClick={toggleEmojiPicker}
                variant="outline"
                className={styles.icon}
                aria-label={t('community.albumEmoji', 'Ajouter un emoji au titre')}
              >
                <IconMoodSmile size={20} />
              </ActionIcon>
            </Tooltip>
          ) : null}

          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>

          <Button onClick={onPublish} loading={isLoadingEdit || isLoadingCreate}>
            {feedItem
              ? t('community.albumSave', 'Enregistrer')
              : t('community.albumPublish', "Publier l'album")}
          </Button>

          <div className={styles.emojiPicker}>
            <EmojiPicker visible={isEmojiPickerOpen} onEmoji={onEmoji} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemoryModalEdit;
